import { useRef, useState, useMemo, useCallback, type JSX } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type Context from '@alltrails/core/types/Context';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import {
  COLOR_TEXT_PRIMARY,
  COLOR_BORDER_SEPARATOR,
  FONT_SIZE_50,
  FONT_FAMILY_PRIMARY,
  SPACE_200,
  SPACE_300,
  SPACE_100,
  COLOR_BORDER_INTERACTIVE
} from '@alltrails/denali/tokens';
import { metersToKilometers, metersToMiles } from '@alltrails/data-formatting';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useElevationChart from '../../hooks/useElevationChart';
import type { ElevationPoint, LngLat } from '../../types/Geo';
import ElevationTooltip from './ElevationTooltip';
import styles from './styles/styles.module.scss';

export type ElevationChartProps = {
  elevationData: ElevationPoint[][]; // An array of segments, where each segment is an array of elevation points
  handleMouseOver: (coordinates: LngLat | null) => void;
  activeSegmentIndex?: number;
};

const ElevationChart = ({ elevationData, handleMouseOver, activeSegmentIndex }: ElevationChartProps): JSX.Element | null => {
  const intl = useIntl();
  const [currentGrade, setCurrentGrade] = useState<number | string>('');
  const isMobile = useIsMobileSizedScreen();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { hasPermission } = useAuthorization();
  const isAdmin = hasPermission({ permission: 'trails:manage' });

  const { displayMetric } = useSelector((state: { context: Context }) => ({
    displayMetric: state.context.displayMetric
  }));

  const { data, handleMouseMove, showEmptyData, tooltipXPosition, xTicks, yTicks } = useElevationChart({
    displayMetric,
    elevationData,
    handleMouseOver,
    setCurrentGrade,
    shouldRenderMinimalTicks: isMobile,
    tooltipRef,
    activeSegmentIndex
  });

  const lineMargin = useMemo(() => {
    const responsiveMargin = isMobile ? SPACE_200 : SPACE_300;

    return {
      top: SPACE_200,
      right: responsiveMargin,
      bottom: SPACE_200,
      left: responsiveMargin
    };
  }, [isMobile]);

  // Show a reference line at the end of each segment
  const referenceLineXValues = useMemo(() => {
    return elevationData.reduce<number[]>((values, segment, index) => {
      if (index === elevationData.length - 1) {
        // Don't need a reference line for the final segment
        return values;
      }
      if (segment.length > 1) {
        const distance = segment[segment.length - 1][0];
        values.push(displayMetric ? metersToKilometers(distance) : metersToMiles(distance));
      }
      return values;
    }, []);
  }, [displayMetric, elevationData]);

  const xAxisTickFormatter = useCallback(
    (value: number) => {
      return intl.formatNumber(value, {
        style: 'unit',
        unit: displayMetric ? 'kilometer' : 'mile',
        notation: 'compact',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      });
    },
    [displayMetric, intl]
  );

  const yAxisTickFormatter = useCallback(
    (value: number) => {
      return intl.formatNumber(value, {
        style: 'unit',
        unit: displayMetric ? 'meter' : 'foot',
        maximumFractionDigits: 0
      });
    },
    [displayMetric, intl]
  );

  const yDomain = useMemo(() => {
    // The y-ticks already have a little buffer built in to them but we want to add a little more to make room for the tick labels.
    // We don't put all the buffer on the ticks because we don't want the tick values to be that far away from the actual data values.
    // We don't put all the buffer on just the domain because then the tick labels render too far away from the correct value.
    const range = yTicks[1] - yTicks[0];
    const buffer = range * 0.15;
    return [yTicks[0] - buffer, yTicks[1] + buffer];
  }, [yTicks]);

  return (
    <ResponsiveContainer>
      <LineChart className={styles.lineChart} data={data} margin={lineMargin} onMouseMove={handleMouseMove}>
        <ReferenceLine className={styles.topLine} y={yDomain[1]} />
        <ReferenceLine className={styles.midLine} y={yDomain[0] + (yDomain[1] - yDomain[0]) / 2} />
        <XAxis
          allowDataOverflow
          // className={styles.xAxis} This is not working in recharts 3.x. The class is not applied to the X-axis.
          dataKey="distance"
          domain={[xTicks[0], xTicks[xTicks.length - 1]]}
          interval={0} // Make sure all ticks render
          tickFormatter={xAxisTickFormatter}
          tickLine={false}
          tickMargin={SPACE_100}
          ticks={xTicks}
          type="number"
        />
        <YAxis
          axisLine={false}
          className={styles.yAxis}
          dataKey="elevation"
          domain={yDomain}
          mirror
          orientation="right"
          tickFormatter={yAxisTickFormatter}
          tickLine={false}
          tickMargin={0}
          ticks={yTicks}
          tickSize={0}
          type="number"
        />
        {showEmptyData && (
          <text
            x="50%"
            y="35%"
            dy={8}
            style={{ fontSize: FONT_SIZE_50, fill: COLOR_TEXT_PRIMARY, fontFamily: FONT_FAMILY_PRIMARY }}
            width={200}
            textAnchor="middle"
          >
            <FormattedMessage defaultMessage="No elevation data" />
          </text>
        )}
        <Tooltip
          content={<ElevationTooltip ref={tooltipRef} grade={currentGrade} displayMetric={displayMetric} isAdmin={isAdmin} />}
          isAnimationActive={false}
          position={{ x: tooltipXPosition, y: lineMargin.top + SPACE_100 }}
        />
        {referenceLineXValues.map((x, index) => (
          <ReferenceLine
            data-testid={`reference-line-${index}`}
            key={x}
            x={x}
            stroke={COLOR_BORDER_SEPARATOR}
            strokeWidth={1}
            strokeDasharray="2 2"
          />
        ))}
        <Line
          hide={showEmptyData} // hide the line but show the axes and ticks
          type="linear"
          dataKey="elevation"
          stroke={COLOR_TEXT_PRIMARY}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
        {activeSegmentIndex !== undefined && (
          // All points are rendered in the main line but we render points from the inactive segment in a line with a different color above the main line to hide them
          <Line
            hide={showEmptyData} // hide the line but show the axes and ticks
            type="linear"
            dataKey="inactiveElevation"
            stroke={COLOR_BORDER_INTERACTIVE}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default ElevationChart;
